import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Printer, Save, FileText, FileCode, FolderOpen, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { parseTallyXmlText, decodeTallyBytes, fillMissingRate } from '../utils/tallyXmlImport';
import CreditEntryReviewTable from '../components/CreditEntryReviewTable';
import ImportCheckPanel from '../components/ImportCheckPanel';
import { flagEntries, checkInvoice, withoutWarnings } from '../utils/entryFlags';

const FUEL_PRODUCTS = ['Petrol', 'Diesel', 'Xtragreen'];

// Standard GST state codes -> state name, derived from the GSTIN's first 2 digits
const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
  '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur',
  '15': 'Mizoram', '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '25': 'Daman and Diu', '26': 'Dadra and Nagar Haveli', '27': 'Maharashtra', '28': 'Andhra Pradesh',
  '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh',
  '38': 'Ladakh'
};

function placeOfSupplyFromGstin(gstin) {
  if (!gstin || gstin.length < 2) return null;
  const code = gstin.substring(0, 2);
  const state = GST_STATE_CODES[code];
  return state ? `${state} (${code})` : null;
}

// If the same vehicle got fuel AND a cash advance on the same day, fold the cash
// line into the fuel line so it prints as one row (fuel columns + Cash Amount),
// same as the paper register does.
const isCashLine = (e) => (e.item_name || '').toUpperCase() === 'CASH';

// "AE/26-27/5272" -> 5272 (the trailing number of a Tally voucher no.)
const voucherSerial = (v) => {
  const m = String(v || '').match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : null;
};

// Cash and fuel lines for the same vehicle on the same day are folded together only
// when they pair up cleanly - the same number of each. With one of each that's
// obvious; with several (e.g. 2 fills and 2 cash advances) each cash line goes with
// the fuel line whose voucher number is closest, since the register enters a driver's
// cash right next to his fill. Anything that doesn't pair evenly is left as it is.
function mergeSameVehicleCashLines(entries) {
  const groups = {};
  entries.forEach(e => {
    if (!e.vehicle_no) return;
    const key = `${e.business_date}|${e.vehicle_no}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });

  const absorbedCashIds = new Set();
  const cashAddedToFuel = {};

  Object.values(groups).forEach(group => {
    const cashLines = group.filter(isCashLine);
    const fuelLines = group.filter(e => !isCashLine(e));
    if (cashLines.length === 0 || cashLines.length !== fuelLines.length) return;

    const pairs = [];
    cashLines.forEach(c => fuelLines.forEach(f => {
      const a = voucherSerial(c.voucher_no);
      const b = voucherSerial(f.voucher_no);
      pairs.push({ c, f, distance: a !== null && b !== null ? Math.abs(a - b) : Infinity });
    }));
    pairs.sort((x, y) => x.distance - y.distance);

    const usedCash = new Set();
    const usedFuel = new Set();
    pairs.forEach(({ c, f }) => {
      if (usedCash.has(c.id) || usedFuel.has(f.id)) return;
      usedCash.add(c.id);
      usedFuel.add(f.id);
      absorbedCashIds.add(c.id);
      cashAddedToFuel[f.id] = (cashAddedToFuel[f.id] || 0) + (parseFloat(c.cash_amount) || 0);
    });
  });

  const merged = [];
  entries.forEach(e => {
    if (absorbedCashIds.has(e.id)) return;
    if (cashAddedToFuel[e.id] !== undefined) {
      merged.push({ ...e, cash_amount: (parseFloat(e.cash_amount) || 0) + cashAddedToFuel[e.id] });
    } else {
      merged.push(e);
    }
  });
  return merged;
}

const Invoice = () => {
  const [creditors, setCreditors] = useState([]);
  const [party, setParty] = useState('');
  const [discountProduct, setDiscountProduct] = useState('Diesel');
  // 'combined' = a same-day cash advance to the same vehicle is folded into that fuel
  // row's Cash Amount column (the original layout). 'separate' = fuel rows only in the
  // fuel table, every cash advance listed in its own "Cash Given to Driver" table.
  const [invoiceFormat, setInvoiceFormat] = useState('combined');
  // For re-issuing an invoice that went out with a mistake: prints the ORIGINAL number and a
  // "Corrected Copy" label, and never assigns a new number (a second number would make the
  // same sale appear twice in the records).
  const [correctedOf, setCorrectedOf] = useState('');
  const [correctedDate, setCorrectedDate] = useState('');
  const [discountPerLitre, setDiscountPerLitre] = useState('');
  const [tfrToIocl, setTfrToIocl] = useState('');

  const [profile, setProfile] = useState({});
  const [invoiceNo, setInvoiceNo] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [showSaved, setShowSaved] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState(null);
  const [savedFilter, setSavedFilter] = useState('');
  const [openingId, setOpeningId] = useState(null);

  const [lines, setLines] = useState([]);
  const [receipts, setReceipts] = useState([]);
  // Import check: what the importer left out, Tally's own total, and whether the person
  // has confirmed the items it wasn't sure about (reset whenever the set of items changes)
  const [skippedVouchers, setSkippedVouchers] = useState([]);
  const [importTotal, setImportTotal] = useState(null);
  const [ackSignature, setAckSignature] = useState('');
  const [trustWarnings, setTrustWarnings] = useState(false); // a reopened saved invoice was already checked when saved
  // What the invoice looked like when it was last saved/opened - see unsavedChanges below
  const [savedFingerprint, setSavedFingerprint] = useState(null);
  const markSavedRef = useRef(false);

  useEffect(() => {
    fetch('/api/creditors', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(res => res.json())
      .then(rows => { if (!rows.error) setCreditors(rows); })
      .catch(err => console.error('Failed to fetch creditors', err));

    fetch('/api/settings/profile', { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      .then(res => res.json())
      .then(p => { if (!p.error) setProfile(p); })
      .catch(err => console.error('Failed to fetch pump profile', err));
  }, []);

  const handleXmlUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!party) {
      toast.error('Pick a creditor above first.');
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const { entries, receipts: importedReceipts, skipped, importTotal: tallyTotal, error } = await parseTallyXmlText(decodeTallyBytes(event.target.result), party);
        if (error) {
          toast.error(error);
          return;
        }
        setLines(entries);
        setReceipts(importedReceipts || []);
        setSkippedVouchers(skipped || []);
        setImportTotal(tallyTotal ?? null);
        setAckSignature('');
        setTrustWarnings(false);
        setInvoiceNo(null);
        setInvoiceId(null);
        setSavedFingerprint(null);
        const receiptNote = importedReceipts?.length ? ` and ${importedReceipts.length} receipt(s)` : '';
        toast.success(`Extracted ${entries.length} entries${receiptNote}. Check them below - the invoice updates as you edit.`);
      } catch (err) {
        toast.error('Error parsing Tally XML file.');
        console.error(err);
      }
    };
    reader.onerror = () => toast.error('Could not read that file.');
    // The encoding (usually UTF-16, sometimes UTF-8) is worked out from the bytes
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  const handleLineFieldChange = (id, field, value) => {
    setLines(prev => prev.map(l => (l.id === id ? { ...l, [field]: value } : l)));
    // a line imported with an unreadable date had no rate to work its quantity from - now it has a date
    if (field === 'business_date') {
      const line = lines.find(l => l.id === id);
      fillMissingRate(line, value, line?.item_name, setLines);
    }
  };

  // Fuel rows keep their rupee value in `amount`, CASH rows keep it in `cash_amount` -
  // the invoice tables each only read one of the two. Switching the Item dropdown between
  // a fuel product and CASH has to carry the value across, or it just silently zeroes out
  // on whichever side isn't being read anymore.
  const handleLineItemChange = (id, newItemName) => {
    // A line whose item had to be chosen by hand has no quantity/rate (the importer couldn't tell
    // the product) - work them out from that day's rate rather than leave it for typing
    fillMissingRate(lines.find(l => l.id === id), lines.find(l => l.id === id)?.business_date, newItemName, setLines);
    setLines(prev => prev.map(l => {
      if (l.id !== id) return l;
      const wasCash = (l.item_name || '').toUpperCase() === 'CASH';
      const willBeCash = newItemName.toUpperCase() === 'CASH';
      if (wasCash && !willBeCash) {
        return { ...l, item_name: newItemName, amount: l.cash_amount || 0, cash_amount: 0, cash_source: null };
      }
      if (!wasCash && willBeCash) {
        // Only `amount` moves to `cash_amount` (that's what the two invoice tables
        // actually read) - quantity/rate are left alone rather than zeroed, so
        // switching back to a fuel product later doesn't lose them. Choosing CASH by
        // hand is a decision, so the import check stops second-guessing the line.
        return { ...l, item_name: newItemName, cash_amount: l.amount || 0, amount: 0, cash_source: 'manual' };
      }
      return { ...l, item_name: newItemName };
    }));
  };

  const separateCash = invoiceFormat === 'separate';
  const combinedLines = useMemo(
    () => (separateCash ? lines : mergeSameVehicleCashLines(lines)),
    [lines, separateCash]
  );
  // Only a line whose Item is a fuel product or CASH has a table to print in. A line with
  // no valid Item must not be added to the totals either, or the invoice bills money that
  // appears nowhere on it - those lines are listed in a warning above instead.
  const isPrintable = (e) => FUEL_PRODUCTS.includes(e.item_name) || e.item_name === 'CASH';
  const mergedLines = useMemo(() => combinedLines.filter(isPrintable), [combinedLines]);

  // Import check: flags every line the importer could not place or was unsure about
  const flagInfo = useMemo(() => flagEntries(lines, { separateCash }), [lines, separateCash]);

  const productTotals = useMemo(() => {
    const totals = {};
    mergedLines.forEach(e => {
      const key = e.item_name || 'Other';
      if (!totals[key]) totals[key] = { quantity: 0, amount: 0 };
      totals[key].quantity += parseFloat(e.quantity) || 0;
      totals[key].amount += parseFloat(e.amount) || 0;
    });
    return totals;
  }, [mergedLines]);

  const totalAmount = useMemo(() => mergedLines.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0), [mergedLines]);
  const totalCash = useMemo(() => mergedLines.reduce((s, e) => s + (parseFloat(e.cash_amount) || 0), 0), [mergedLines]);

  const dateRange = useMemo(() => {
    const dates = mergedLines.map(e => e.business_date).filter(Boolean).sort();
    return dates.length > 0 ? { from: dates[0], to: dates[dates.length - 1] } : null;
  }, [mergedLines]);

  const totals = useMemo(() => {
    const discountQty = productTotals[discountProduct]?.quantity || 0;
    const discountRate = parseFloat(discountPerLitre) || 0;
    // Rounded to the paisa first: the box prints 2 decimals, so G.TOTAL must be worked out
    // from the same rounded figure it shows (977.01 L x 0.50 = 488.505 -> 488.51).
    const discountAmount = Math.round(discountQty * discountRate * 100) / 100;
    const tfr = parseFloat(tfrToIocl) || 0;
    const total = totalAmount + totalCash;
    const grandTotal = total - discountAmount - tfr;
    return { discountQty, discountAmount, tfr, total, grandTotal };
  }, [productTotals, discountProduct, discountPerLitre, tfrToIocl, totalAmount, totalCash]);

  const totalReceived = useMemo(() => receipts.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), [receipts]);
  const balanceDue = totals.grandTotal - totalReceived;

  // Everything the printed invoice is built from, so it can be reopened exactly as it was
  const buildSnapshot = () => ({
    party, lines, receipts, invoiceFormat, discountProduct, discountPerLitre, tfrToIocl, profile
  });

  // An invoice that has a number is the saved copy. If it is edited afterwards, what prints would no
  // longer match what is stored - so printing waits until Save Changes (the pump profile is left out:
  // it loads separately and is not something the person edits here).
  const fingerprint = JSON.stringify({ ...buildSnapshot(), profile: null });
  useEffect(() => {
    if (markSavedRef.current) {
      markSavedRef.current = false;
      setSavedFingerprint(fingerprint);
    }
  }, [fingerprint]);
  const unsavedChanges = !!invoiceId && savedFingerprint !== null && fingerprint !== savedFingerprint;

  // Import check, continued: the invoice-level checks (discount, totals, receipts) join the
  // per-line ones. Errors block printing and saving; warnings need the "I have checked" tick.
  const invoiceIssues = useMemo(() => checkInvoice({
    fuelQuantities: Object.fromEntries(Object.entries(productTotals).map(([k, v]) => [k, v.quantity])),
    discountProduct, discountPerLitre, totals, receipts, balanceDue
  }), [productTotals, discountProduct, discountPerLitre, totals, receipts, balanceDue]);
  const unsavedItem = unsavedChanges ? [{
    scope: 'invoice', level: 'error', code: 'UNSAVED_CHANGES',
    message: `Invoice ${invoiceNo} has been changed since it was saved. Click "Save Changes" so the stored copy matches what you print.`
  }] : [];
  const checkErrors = [...flagInfo.errors, ...invoiceIssues.filter(i => i.level === 'error'), ...unsavedItem];
  const checkWarnings = [...flagInfo.warnings, ...invoiceIssues.filter(i => i.level === 'warning')];
  const warningSignature = checkWarnings.map(w => `${w.id ?? 'invoice'}:${w.code}`).join('|')
    + '#' + skippedVouchers.filter(s => s.level === 'warn').map(s => s.voucher_no).join('|');
  const hasWarnings = checkWarnings.length > 0 || skippedVouchers.some(s => s.level === 'warn');
  const warningsAcknowledged = trustWarnings || (hasWarnings && ackSignature === warningSignature);
  // checkBlocked stops saving / assigning a number; printing is also stopped by unsaved edits
  // (those are fixed BY saving, so they must not block the Save button itself)
  const checkBlocked = lines.length > 0 && (checkErrors.length - unsavedItem.length > 0 || (hasWarnings && !warningsAcknowledged));
  const printBlocked = checkBlocked || unsavedChanges;
  // The warnings are settled once confirmed (ticked, or a reopened invoice that was checked when saved)
  // and nothing real is wrong - they then stop looking like problems, on the panel and on the rows
  const warningsSettled = hasWarnings && warningsAcknowledged && checkErrors.length - unsavedItem.length === 0;
  const checkBlockedReason = checkErrors.length - unsavedItem.length > 0
    ? 'Fix the red problems in the import check first'
    : 'Tick "I have checked the items above" in the import check first';
  const printBlockedReason = checkBlocked ? checkBlockedReason : 'Click Save Changes first - the invoice was edited after it was saved';

  const authHeaders = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

  const fetchSavedInvoices = async () => {
    try {
      const res = await fetch('/api/invoices', { headers: authHeaders });
      const rows = await res.json();
      if (Array.isArray(rows)) setSavedInvoices(rows);
      else toast.error(rows.error || 'Could not load saved invoices');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleSaved = () => {
    const next = !showSaved;
    setShowSaved(next);
    if (next) fetchSavedInvoices();
  };

  const openSavedInvoice = async (id) => {
    setOpeningId(id);
    try {
      const res = await fetch(`/api/invoices/${id}`, { headers: authHeaders });
      const row = await res.json();
      if (row.error) {
        toast.error(row.error);
      } else if (!row.snapshot) {
        toast.error('This invoice was saved before copies were kept, so it cannot be reopened.');
      } else {
        const s = row.snapshot;
        setParty(s.party || row.party_name);
        setLines(s.lines || []);
        setReceipts(s.receipts || []);
        setInvoiceFormat(s.invoiceFormat || 'combined');
        setDiscountProduct(s.discountProduct || 'Diesel');
        setDiscountPerLitre(s.discountPerLitre ?? '');
        setTfrToIocl(s.tfrToIocl ?? '');
        if (s.profile && Object.keys(s.profile).length) setProfile(s.profile);
        setInvoiceNo(row.invoice_no);
        setInvoiceId(row.id);
        setCorrectedOf('');
        setCorrectedDate('');
        markSavedRef.current = true;
        setSkippedVouchers([]);
        setImportTotal(null);
        setTrustWarnings(true); // it was checked when it was saved; hard errors still block
        setShowSaved(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        toast.success(`Opened invoice ${row.invoice_no} - use Print / Save as PDF to download it again.`);
      }
    } catch (err) {
      toast.error(err.message);
    }
    setOpeningId(null);
  };

  const handleGenerateInvoiceNo = async () => {
    if (mergedLines.length === 0 || !dateRange) return;
    setIsSaving(true);
    try {
      // An invoice that already has a number is updated in place - saving again must
      // never hand out a second invoice number for the same invoice.
      const res = await fetch(invoiceId ? `/api/invoices/${invoiceId}` : '/api/invoices', {
        method: invoiceId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          party_name: party,
          date_from: dateRange.from,
          date_to: dateRange.to,
          discount_per_litre: parseFloat(discountPerLitre) || 0,
          tfr_to_iocl: totals.tfr,
          total_amount: totalAmount,
          cash_amount: totalCash,
          discount_amount: totals.discountAmount,
          grand_total: totals.grandTotal,
          snapshot: buildSnapshot()
        })
      });
      const json = await res.json();
      if (json.status === 'success') {
        const wasUpdate = !!invoiceId;
        setInvoiceNo(json.invoice_no);
        setInvoiceId(json.id);
        setSavedFingerprint(fingerprint);
        toast.success(wasUpdate ? `Invoice ${json.invoice_no} updated` : `Invoice ${json.invoice_no} generated and saved`);
        if (showSaved) fetchSavedInvoices();
      } else {
        toast.error(json.error || 'Failed to save invoice');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setIsSaving(false);
  };

  const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => {
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
  };

  const productGroups = useMemo(() => (
    FUEL_PRODUCTS.filter(p => productTotals[p]).map(p => ({
      name: p,
      rows: mergedLines.filter(e => e.item_name === p)
    }))
  ), [mergedLines, productTotals]);

  const cashRows = useMemo(() => mergedLines.filter(e => e.item_name === 'CASH'), [mergedLines]);

  const placeOfSupply = placeOfSupplyFromGstin(profile.gstin);
  const hasInvoice = mergedLines.length > 0 && dateRange;

  return (
    <div className="max-w-5xl mx-auto pb-16">
      {/* Controls - hidden when printing */}
      <div className="no-print space-y-4 mb-6 glass-panel p-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-indigo-600" /> Invoice Generator
        </h1>
        <p className="text-sm text-slate-500 -mt-2">Import a Tally XML for a creditor, check the lines, and generate the printable invoice - nothing here is pulled from past records.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Creditor (Billed to)</label>
            <select className="input-field" value={party} onChange={e => { setParty(e.target.value); setLines([]); setReceipts([]); setInvoiceNo(null); setInvoiceId(null); }}>
              <option value="">Select creditor...</option>
              {creditors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Discount applies to</label>
            <select className="input-field" value={discountProduct} onChange={e => setDiscountProduct(e.target.value)}>
              {FUEL_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Discount per Litre (₹)</label>
            <input type="number" step="0.01" className="input-field" placeholder="e.g. 0.50" value={discountPerLitre} onChange={e => setDiscountPerLitre(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">TFR to IOCL (₹)</label>
            <input type="number" className="input-field" placeholder="0" value={tfrToIocl} onChange={e => setTfrToIocl(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Invoice format</label>
            <select className="input-field" value={invoiceFormat} onChange={e => setInvoiceFormat(e.target.value)}>
              <option value="combined">Combined (cash inside diesel table)</option>
              <option value="separate">Separate cash table</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Corrected copy of invoice no. (optional)</label>
            <input className="input-field" placeholder="e.g. 2026-10/8" value={correctedOf} onChange={e => setCorrectedOf(e.target.value)} />
          </div>
          {correctedOf.trim() && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Original invoice dated</label>
              <input type="date" className="input-field" value={correctedDate} onChange={e => setCorrectedDate(e.target.value)} />
            </div>
          )}
        </div>
        <div className="flex gap-3 flex-wrap">
          <label className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm shadow-sm ${party ? 'bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}>
            <FileCode className="w-4 h-4" />
            Import Tally XML
            <input type="file" accept=".xml" className="hidden" disabled={!party} onChange={handleXmlUpload} />
          </label>
          <button
            onClick={handleGenerateInvoiceNo}
            disabled={!hasInvoice || isSaving || !!correctedOf.trim() || checkBlocked}
            title={checkBlocked ? checkBlockedReason : (correctedOf.trim() ? 'A corrected copy keeps the original invoice number - no new number is assigned' : undefined)}
            className="btn-primary flex items-center gap-2 px-4 py-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : (correctedOf.trim() ? 'No new number (corrected copy)' : (invoiceId ? 'Save Changes' : 'Assign Invoice No.'))}
          </button>
          <button onClick={() => window.print()} disabled={!hasInvoice || printBlocked} title={printBlocked ? printBlockedReason : undefined} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium text-sm disabled:opacity-50">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
          <button onClick={toggleSaved} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 font-medium text-sm">
            <FolderOpen className="w-4 h-4" /> {showSaved ? 'Hide Saved Invoices' : 'Saved Invoices'}
          </button>
        </div>
      </div>

      {showSaved && (
        <div className="no-print bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="p-4 border-b border-slate-100 flex items-center gap-3">
            <h2 className="font-bold text-slate-800">Saved Invoices</h2>
            <input
              className="input-field max-w-xs py-1.5 text-sm ml-auto" placeholder="Search party or invoice no..."
              value={savedFilter} onChange={e => setSavedFilter(e.target.value)}
            />
            <button onClick={() => setShowSaved(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          {!savedInvoices ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading...</div>
          ) : (() => {
            const q = savedFilter.trim().toLowerCase();
            const shown = savedInvoices.filter(i => !q || i.party_name.toLowerCase().includes(q) || i.invoice_no.toLowerCase().includes(q));
            return shown.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No saved invoices found.</div>
            ) : (
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-500 text-xs font-semibold sticky top-0">
                    <tr>
                      <th className="p-3">Invoice No.</th>
                      <th className="p-3">Party</th>
                      <th className="p-3">Period</th>
                      <th className="p-3 text-right">Grand Total</th>
                      <th className="p-3">Saved on</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shown.map(i => (
                      <tr key={i.id} className="hover:bg-slate-50/50">
                        <td className="p-3 font-mono font-semibold">{i.invoice_no}</td>
                        <td className="p-3">{i.party_name}</td>
                        <td className="p-3 text-slate-600 whitespace-nowrap">{fmtDate(i.date_from)} to {fmtDate(i.date_to)}</td>
                        <td className="p-3 text-right font-mono">{fmt(i.grand_total)}</td>
                        <td className="p-3 text-slate-500 whitespace-nowrap">{new Date(i.created_at).toLocaleDateString('en-IN')}</td>
                        <td className="p-3 text-right">
                          {i.reopenable ? (
                            <button onClick={() => openSavedInvoice(i.id)} disabled={openingId === i.id} className="text-indigo-600 hover:text-indigo-800 font-semibold disabled:opacity-50">
                              {openingId === i.id ? 'Opening...' : 'Open'}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400" title="Saved before copies were kept">No copy</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {lines.length > 0 && (
        <div className="no-print bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="p-5 border-b border-slate-100 bg-indigo-50/50">
            <h2 className="font-bold text-indigo-900">Check these - the invoice below updates as you edit</h2>
          </div>
          <ImportCheckPanel
            errors={checkErrors}
            warnings={checkWarnings}
            skipped={skippedVouchers}
            importTotal={importTotal}
            invoiceTotal={lines.reduce((s, e) => s + (parseFloat(e.amount) || 0) + (parseFloat(e.cash_amount) || 0), 0)}
            acknowledged={warningsAcknowledged}
            confirmed={warningsSettled}
            trusted={trustWarnings}
            onAcknowledge={(checked) => setAckSignature(checked ? warningSignature : '')}
          />
          <CreditEntryReviewTable
            entries={lines}
            showParty={false}
            flags={warningsSettled ? withoutWarnings(flagInfo.byId) : flagInfo.byId}
            onChangeField={handleLineFieldChange}
            onChangeItemName={handleLineItemChange}
            onDelete={(id) => setLines(prev => prev.filter(e => e.id !== id))}
          />
          {receipts.length > 0 && (
            <div className="border-t border-slate-200 p-5 bg-emerald-50/40">
              <h3 className="font-bold text-emerald-800 mb-1">Receipts from this creditor</h3>
              <p className="text-xs text-slate-500 mb-3">Shown at the bottom of the invoice as "Less: Received" and taken off the balance due. Delete any that shouldn't be on this invoice.</p>
              <div className="space-y-2">
                {receipts.map(r => (
                  <div key={r.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-2 text-sm">
                    <input type="date" className="bg-transparent border border-transparent hover:border-slate-200 rounded px-2 py-1"
                      value={r.business_date || ''} onChange={e => setReceipts(prev => prev.map(x => x.id === r.id ? { ...x, business_date: e.target.value } : x))} />
                    <span className="text-slate-600 flex-1 truncate">{r.bank || 'Receipt'}{r.voucher_no ? ` (#${r.voucher_no})` : ''}</span>
                    <input type="number" className="w-28 text-right font-mono font-medium bg-transparent border border-transparent hover:border-slate-200 rounded px-2 py-1"
                      value={r.amount ?? ''} onChange={e => setReceipts(prev => prev.map(x => x.id === r.id ? { ...x, amount: e.target.value === '' ? '' : parseFloat(e.target.value) } : x))} />
                    <button onClick={() => setReceipts(prev => prev.filter(x => x.id !== r.id))} className="text-slate-400 hover:text-red-500 px-2">&times;</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {hasInvoice && (
        <div className={`bg-white border-2 border-slate-800 p-6 print-invoice${printBlocked ? ' print-blocked' : ''}`}>
          <div className="flex justify-between items-start border-b-2 border-slate-800 pb-2 mb-2">
            <div className="text-xs font-mono">GSTIN : {profile.gstin || '-'}</div>
            <div className="text-sm font-bold underline">{correctedOf.trim() ? 'Corrected Copy' : 'Original Copy'}</div>
          </div>

          <div className="text-center mb-3">
            <div className="text-lg font-bold underline mb-2">TAX INVOICE</div>
            {correctedOf.trim() && (
              <div className="text-sm font-semibold border border-slate-800 inline-block px-3 py-1 mb-2">
                Corrected copy of Invoice No. {correctedOf.trim()}{correctedDate ? ` dated ${fmtDate(correctedDate)}` : ''} - replaces the earlier copy.
              </div>
            )}
            <div className="flex items-center justify-center gap-3">
              <img src="/assets/indianoil-logo.png" alt="Indian Oil" className="w-14 h-14" />
              <h1 className="text-3xl font-extrabold uppercase">{profile.pump_name || 'Pump Name'}</h1>
            </div>
            <p className="text-sm mt-1">{profile.address}</p>
            <p className="text-sm font-semibold underline">
              Tel. : {profile.phone || '-'} &nbsp;&nbsp;&nbsp; {profile.email || ''}
            </p>
          </div>

          <div className="border-2 border-slate-800 text-center py-2 mb-2">
            <div className="font-bold underline">Bank Detail</div>
            <div className="text-sm">Bank Name: {profile.bank_name || '-'}</div>
            <div className="text-sm">Account No: {profile.bank_account || '-'}</div>
            <div className="text-sm">IFSC Code: {profile.bank_ifsc || '-'}</div>
          </div>

          <div className="invoice-meta-block grid grid-cols-2 border-2 border-slate-800 text-sm">
            <div className="p-2 border-r border-b border-slate-800"><b>Invoice No.</b> : {correctedOf.trim() || invoiceNo || '(not yet saved)'}</div>
            <div className="p-2 border-b border-slate-800"><b>Place of Supply</b> : {placeOfSupply || '-'}</div>
            <div className="p-2 border-r border-b border-slate-800"><b>Date of Invoice</b> : {fmtDate(dateRange.from)} To- {fmtDate(dateRange.to)}</div>
            <div className="p-2 border-b border-slate-800"><b>Reverse Charges</b> : NA</div>
            <div className="p-2 border-r col-span-1"><b>Billed to</b> :- {party}</div>
            <div className="p-2"><b>Shipped to</b> :-</div>
          </div>

          {productGroups.map(group => (
            <div key={group.name} className="mt-4">
              <div className="text-center text-xl font-bold border-2 border-b-0 border-slate-800 py-1">{group.name === 'Diesel' ? 'HSD' : group.name === 'Petrol' ? 'MS' : group.name}</div>
              <table className="w-full border-collapse border-2 border-slate-800 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-800 p-1">S.No.</th>
                    <th className="border border-slate-800 p-1">Date</th>
                    <th className="border border-slate-800 p-1">Vehicle No.</th>
                    <th className="border border-slate-800 p-1">Voucher No.</th>
                    <th className="border border-slate-800 p-1">Quantity</th>
                    <th className="border border-slate-800 p-1">Rate</th>
                    <th className="border border-slate-800 p-1">Amount</th>
                    {!separateCash && <th className="border border-slate-800 p-1">Cash Amount</th>}
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((r, idx) => (
                    <tr key={r.id}>
                      <td className="border border-slate-800 p-1 text-center">{idx + 1}</td>
                      <td className="border border-slate-800 p-1 text-center">{fmtDate(r.business_date)}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.vehicle_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.voucher_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.quantity || 0).toFixed(2)}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.rate || 0).toFixed(2)}</td>
                      <td className="border border-slate-800 p-1 text-right">{Math.round(r.amount || 0)}</td>
                      {!separateCash && <td className="border border-slate-800 p-1 text-right">{Number(r.cash_amount || 0).toFixed(2)}</td>}
                    </tr>
                  ))}
                  <tr className="font-bold">
                    <td colSpan={4} className="border border-slate-800 p-1 text-right">TOTAL {group.name === 'Diesel' ? 'HSD' : group.name.toUpperCase()} :-</td>
                    <td className="border border-slate-800 p-1 text-right">{productTotals[group.name].quantity.toFixed(2)}</td>
                    <td className="border border-slate-800 p-1"></td>
                    <td className="border border-slate-800 p-1"></td>
                    {!separateCash && <td className="border border-slate-800 p-1"></td>}
                  </tr>
                </tbody>
              </table>
            </div>
          ))}

          {cashRows.length > 0 && (
            <div className="mt-4">
              <div className="text-center text-xl font-bold border-2 border-b-0 border-slate-800 py-1">CASH GIVEN TO DRIVER</div>
              <table className="w-full border-collapse border-2 border-slate-800 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-800 p-1">S.No.</th>
                    <th className="border border-slate-800 p-1">Date</th>
                    <th className="border border-slate-800 p-1">Vehicle No.</th>
                    <th className="border border-slate-800 p-1">Voucher No.</th>
                    <th className="border border-slate-800 p-1">Cash Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {cashRows.map((r, idx) => (
                    <tr key={r.id}>
                      <td className="border border-slate-800 p-1 text-center">{idx + 1}</td>
                      <td className="border border-slate-800 p-1 text-center">{fmtDate(r.business_date)}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.vehicle_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-center">{r.voucher_no || '-'}</td>
                      <td className="border border-slate-800 p-1 text-right">{Number(r.cash_amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                  {separateCash && (
                    <tr className="font-bold">
                      <td colSpan={4} className="border border-slate-800 p-1 text-right">TOTAL CASH :-</td>
                      <td className="border border-slate-800 p-1 text-right">{fmt(totalCash)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="invoice-footer-block">
            <div className="mt-4 flex justify-between items-start gap-6">
              <div className="text-xs text-slate-500 max-w-xs">
                This is a computer generated tax invoice.
              </div>
              <table className="border-collapse border-2 border-slate-800 text-sm min-w-[320px]">
                <tbody>
                  <tr><td className="border border-slate-800 p-1 font-bold">{productGroups.length === 1 ? (productGroups[0].name === 'Diesel' ? 'HSD' : productGroups[0].name.toUpperCase()) : 'FUEL'} AMOUNT</td><td className="border border-slate-800 p-1 text-right">{fmt(totalAmount)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">CASH AMOUNT</td><td className="border border-slate-800 p-1 text-right">{fmt(totalCash)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">TOTAL</td><td className="border border-slate-800 p-1 text-right">{fmt(totals.total)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">DISCOUNT @{parseFloat(discountPerLitre || 0).toFixed(2)}</td><td className="border border-slate-800 p-1 text-right">{fmt(totals.discountAmount)}</td></tr>
                  <tr><td className="border border-slate-800 p-1 font-bold">TFR TO IOCL</td><td className="border border-slate-800 p-1 text-right">{fmt(totals.tfr)}</td></tr>
                  <tr className="text-base"><td className="border border-slate-800 p-1 font-extrabold">G.TOTAL</td><td className="border border-slate-800 p-1 text-right font-extrabold">{fmt(totals.grandTotal)}</td></tr>
                  {receipts.length > 0 && receipts.map(r => (
                    <tr key={r.id}>
                      <td className="border border-slate-800 p-1">Less: Received {r.business_date ? fmtDate(r.business_date) : ''}{r.bank ? ` (${r.bank})` : ''}</td>
                      <td className="border border-slate-800 p-1 text-right">-{fmt(r.amount)}</td>
                    </tr>
                  ))}
                  {receipts.length > 0 && (
                    <tr className="text-base"><td className="border border-slate-800 p-1 font-extrabold">BALANCE DUE</td><td className="border border-slate-800 p-1 text-right font-extrabold">{fmt(balanceDue)}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-10 flex justify-end">
              <div className="text-center">
                <p className="font-bold">{profile.pump_name}</p>
                <p className="mt-8">Manager</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seen only if someone prints (Ctrl+P) while the import check still has open problems */}
      {printBlocked && (
        <div className="print-blocked-notice">
          NOT PRINTED - this invoice still has unresolved items in the import check, or was edited after it was saved. Fix them (or tick "I have checked", or click Save Changes) on the Invoice page, then print again.
        </div>
      )}

      <style>{`
        .print-blocked-notice { display: none; }
        @media print {
          .no-print, nav, aside, header { display: none !important; }
          .print-blocked { display: none !important; }
          .print-blocked-notice { display: block; font-size: 20px; font-weight: bold; border: 3px solid #b91c1c; color: #b91c1c; padding: 24px; margin: 24px; }
          .print-invoice { border: none !important; }
          body { background: white; }
          /* The app shell wraps pages in a fixed-height, scrollable <main> (h-screen +
             overflow-y-auto) for normal browsing. Printing doesn't paginate scrollable
             overflow - it just clips to whatever fits in that box - so a long invoice
             gets silently cut off (e.g. the G.Total row and signature never appear).
             Release those constraints here so the full page can flow across print pages. */
          html, body, #root { height: auto !important; overflow: visible !important; }
          .h-screen { height: auto !important; }
          .overflow-hidden, .overflow-y-auto, .overflow-x-hidden { overflow: visible !important; }

          /* Without this, a table row (or a small block like the totals table) can get
             sliced in half wherever the page happens to end - e.g. an amount's number
             printing on page 1 while the rest of that row lands on page 2. Keep each
             row, and the small footer/meta blocks, as one unbreakable unit instead. */
          .print-invoice tr { break-inside: avoid; page-break-inside: avoid; }
          .print-invoice thead { display: table-header-group; }
          .invoice-meta-block, .invoice-footer-block {
            break-inside: avoid; page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default Invoice;
